import React from 'react';
import { t } from '../../../locales/translate';
import { history } from '../../../model/history';
import { venuePath } from '../../../routes';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { useMutationHandler, useQueryHandler } from '../../../model/graphql';
import { StatusPill } from '../../../components/StatusPill/StatusPill';
import { graphql } from '../../../generated';
import { ShowVenueLayoutQuery } from '../../../generated/graphql';
import { HeaderCard } from '../../../components/HeaderCard/HeaderCard';
import { Button } from '../../../components/Button/Button';
import { Stack } from '@nerdgeschoss/shimmer-component-stack';
import { useModalPresenter } from '../../../components/Modal/PresentedModal';
import { CreateEvent } from '../Events/create/create';

const DESTROY_VENUE_LAYOUT = graphql(`
  mutation DestroyVenueLayout($id: ID!) {
    destroyVenueLayout(input: { id: $id }) {
      venue {
        id
        slug
      }
    }
  }
`);

const GET_EVENT_CREATE_PERMISSIONS = graphql(`
  query GetEventCreatePermissions {
    events {
      permissions {
        create
      }
    }
  }
`);

interface Props {
  venueLayout: ShowVenueLayoutQuery['venueLayout'];
}

export function GeneralInfoCard({ venueLayout }: Props): JSX.Element {
  const modal = useModalPresenter();
  const { confirm: destroyVenueLayoutConfirm } = useConfirmation({
    title: t.general.confirmDelete(t.model.venueLayout._name),
    action: () => destroyVenueLayoutMutation({ variables: { id: venueLayout.id } }),
  });
  const [destroyVenueLayoutMutation] = useMutationHandler(DESTROY_VENUE_LAYOUT, {
    onCompleted: (data) =>
      data.destroyVenueLayout?.venue.slug && history.push(venuePath(data.destroyVenueLayout.venue.slug)),
  });
  const { data: eventPermissionData } = useQueryHandler(GET_EVENT_CREATE_PERMISSIONS);

  const canCreateEvent = eventPermissionData?.events.permissions.create;

  return (
    <HeaderCard
      title={venueLayout.name}
      subTitle={
        <Stack gap={16} align="start">
          {venueLayout.venue.name}
          <Button
            title={t.model.venueLayout.createEventWithThisLayout}
            type="primary"
            onClick={() => modal.present(CreateEvent, { venueLayout }, { size: 'small' })}
            disabled={!canCreateEvent}
          />
        </Stack>
      }
      actions={
        <>
          <PermissionButton
            name="trash"
            model={venueLayout}
            permission="destroy"
            onClick={() => destroyVenueLayoutConfirm()}
            tooltip={t.actions.delete(t.model.venueLayout._name)}
          />
        </>
      }
      titleAdornment={
        <>
          {venueLayout.archived && <StatusPill status="archived" label={t.general.archived} />}
          {venueLayout.importStatus && venueLayout.importStatus !== 'done' && (
            <StatusPill
              status={venueLayout.importStatus}
              label={t.model.venueLayout.importStatus[venueLayout.importStatus]}
            />
          )}
        </>
      }
    />
  );
}
